<script setup lang="ts">
import { computed, ref } from 'vue'

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'

import { useSiteStore } from '@/stores/site'

const siteStore = useSiteStore()

const headerScript = ref('')
const footerScript = ref('')
const scriptsLoaded = ref(false)
const loadingScripts = ref(false)
const baseline = ref('')

const snapshot = () => JSON.stringify({
    headerScript: headerScript.value,
    footerScript: footerScript.value,
})

const dirty = computed(() => scriptsLoaded.value && snapshot() !== baseline.value)

const loadScripts = async () => {
    if (scriptsLoaded.value || loadingScripts.value) return

    loadingScripts.value = true
    try {
        await siteStore.fetchScripts()
        headerScript.value = String(siteStore.scriptHeader || '')
        footerScript.value = String(siteStore.scriptFooter || '')
        baseline.value = snapshot()
        scriptsLoaded.value = true
    } finally {
        loadingScripts.value = false
    }
}

const saveScripts = async () => {
    if (!dirty.value || siteStore.loadingUpdateScripts) return

    siteStore.setScriptHeader(headerScript.value)
    siteStore.setScriptFooter(footerScript.value)

    const saved = await siteStore.handleApplyScript()
    if (saved) baseline.value = snapshot()
}
</script>

<template>
    <Disclosure v-slot="{ open }" as="section" class="beta-global-scripts">
        <DisclosureButton class="beta-global-scripts__toggle" @click="loadScripts">
            <span>
                <strong>Scripts globais do site</strong>
                <small>Códigos aplicados automaticamente em todas as páginas.</small>
            </span>
            <span :class="['beta-global-scripts__toggle-icon', open && 'is-open']" aria-hidden="true">
                +
            </span>
        </DisclosureButton>

        <DisclosurePanel class="beta-global-scripts__content">
            <div v-if="loadingScripts" class="beta-global-scripts__loading">
                Carregando scripts globais…
            </div>

            <form v-else class="beta-global-scripts__form" @submit.prevent="saveScripts">
                <aside>
                    Estes códigos afetam o site inteiro. Para adicionar código em uma única página,
                    use a engrenagem disponível no card da página.
                </aside>

                <label>
                    <span>Código no &lt;head&gt; de todas as páginas</span>
                    <textarea
                        v-model="headerScript"
                        name="globalHeaderScript"
                        rows="7"
                        spellcheck="false"
                        placeholder="<!-- Código inserido dentro da tag <head> -->"
                    ></textarea>
                </label>

                <label>
                    <span>Código antes de &lt;/body&gt; em todas as páginas</span>
                    <textarea
                        v-model="footerScript"
                        name="globalFooterScript"
                        rows="7"
                        spellcheck="false"
                        placeholder="<!-- Código inserido antes do fechamento da tag </body> -->"
                    ></textarea>
                </label>

                <div class="beta-global-scripts__actions">
                    <button type="submit" :disabled="!dirty || siteStore.loadingUpdateScripts">
                        {{ siteStore.loadingUpdateScripts ? 'Salvando…' : 'Salvar scripts globais' }}
                    </button>
                </div>
            </form>
        </DisclosurePanel>
    </Disclosure>
</template>

<style scoped>
.beta-global-scripts {
    overflow: hidden;
    border: 1px solid #e2e8f0;
    border-radius: 17px;
    background: #fbfcfe;
}

.beta-global-scripts__toggle {
    display: flex;
    width: 100%;
    min-height: 62px;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px;
    color: #304761;
    text-align: left;
}

.beta-global-scripts__toggle strong,
.beta-global-scripts__toggle small {
    display: block;
}

.beta-global-scripts__toggle strong {
    font-size: 14px;
    font-weight: 850;
}

.beta-global-scripts__toggle small {
    margin-top: 3px;
    color: #8391a4;
    font-size: 12px;
}

.beta-global-scripts__toggle-icon {
    display: grid;
    width: 28px;
    height: 28px;
    flex: none;
    place-items: center;
    border: 1px solid #dce4ee;
    border-radius: 50%;
    background: #fff;
    color: var(--main-color);
    font-size: 17px;
    transition: 160ms ease;
}

.beta-global-scripts__toggle-icon.is-open {
    border-color: var(--main-color);
    background: var(--main-color);
    color: #fff;
    transform: rotate(45deg);
}

.beta-global-scripts__content {
    border-top: 1px solid #e7ecf2;
    padding: 14px;
}

.beta-global-scripts__loading {
    padding: 24px;
    color: #7e8da2;
    font-size: 12px;
    text-align: center;
}

.beta-global-scripts__form {
    display: grid;
    gap: 12px;
}

.beta-global-scripts__form aside {
    border-radius: 11px;
    background: color-mix(in srgb, var(--main-color) 6%, white);
    padding: 10px 12px;
    color: #667991;
    font-size: 10px;
    line-height: 1.5;
}

.beta-global-scripts__form label {
    display: grid;
    gap: 6px;
}

.beta-global-scripts__form label > span {
    color: #40566f;
    font-size: 12px;
    font-weight: 850;
}

.beta-global-scripts__form textarea {
    width: 100%;
    resize: vertical;
    border: 1px solid #dce4ef;
    border-radius: 11px;
    background: #fff;
    padding: 11px 12px;
    color: #172d4a;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
    line-height: 1.55;
    outline: none;
}

.beta-global-scripts__form textarea:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-global-scripts__actions {
    display: flex;
    justify-content: flex-end;
}

.beta-global-scripts__actions button {
    min-height: 36px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 14px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-global-scripts__actions button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}
</style>
